import React from 'react';
import { Helmet } from 'react-helmet';
import './ManagedServices.css';

const ManagedServices = () => {
    const services = [
        {
            id: 'cdn',
            icon: 'la-network-wired',
            name: 'Managed CDN - Cloudflare',
            tagline: 'Managed CDN & Cloudflare Service',
            description: 'Running your website behind a CDN and need ongoing expert management? We handle full CDN operations – monitoring, optimization, and maintenance – whether your origin server is in our data center or with any provider worldwide, keeping your site fast, stable, and secure.',
            details: [
                'Cloudflare Platform',
                'Global edge network (including Israel)',
                'CDN, Load Balancing, DNS',
                'Advanced security (WAF, DDoS protection)',
                'Video delivery',
                'Management for up to 4 domains',
                'Does not include Cloudflare license/subscription'
            ],
            price: '$139.90',
            period: 'Monthly',
            link: 'https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-cdn',
            featured: false
        },
        {
            id: 'server',
            icon: 'la-server',
            name: 'Public Cloud / External Server Management',
            tagline: 'Public Cloud / External Server Management',
            description: 'We provide full management for a single server hosted on a public cloud platform (such as AWS) or with any other hosting provider – including monitoring, security updates, performance tuning and ongoing maintenance, so your environment stays stable and reliable.',
            details: [
                'Covers one server only (virtual or dedicated)',
                'Smart management agent installation',
                'Continuous monitoring',
                'Regular system updates',
                'Integrated security component',
                'Ongoing support'
            ],
            price: '$139.90',
            period: 'Monthly',
            link: 'https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-external-server',
            featured: false
        },
        {
            id: 'aws',
            icon: 'la-cloud',
            name: 'Managed AWS Auto Scaling & ECS',
            tagline: 'AWS Auto Scaling & High Availability Management',
            description: 'End-to-end management of an AWS environment built for resilience and uptime – including configuration and ongoing operation of Auto Scaling and High Availability (multi-AZ where applicable) to keep your workloads responsive and stable under changing traffic.',
            details: [
                'Covers up to 3 EC2 servers',
                'Auto Scaling configuration',
                'High Availability (multi-AZ)',
                'Amazon ECS container management',
                'Deployment & updates',
                'Basic health monitoring'
            ],
            price: '$714.90',
            period: 'Monthly',
            link: 'https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-aws-auto-scaling-and-ecs-up-to-3-servers',
            featured: false
        },
        {
            id: 'aurora',
            icon: 'la-database',
            name: 'Managed Amazon Aurora Database',
            tagline: 'Managed Amazon Aurora Database',
            description: 'Full management of your Amazon Aurora database instance – including setup, monitoring, optimization, backup management, and performance tuning, ensuring your database runs smoothly and efficiently.',
            details: [
                'Database setup & configuration',
                'Continuous monitoring',
                'Performance optimization',
                'Backup management',
                'Security updates',
                'Query optimization'
            ],
            price: 'Custom',
            period: 'Monthly',
            link: 'https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-amazon-aurora-database',
            featured: false
        },
        {
            id: 'monitoring',
            icon: 'la-chart-line',
            name: 'Managed Centralized Logging & Monitoring',
            tagline: 'Managed Server & Service Monitoring',
            description: 'We provide fully managed monitoring for your servers and critical services, using a smart monitoring agent and a cloud-based dashboard. Our team configures thresholds, watches alerts and handles first-line response so issues are detected early and acted on quickly.',
            details: [
                'Continuous resource monitoring (CPU, RAM, disk, network)',
                'Service and uptime checks (web, database, applications)',
                'Alert handling & triage',
                'Monthly status summary',
                'Up to 8 hours of work per month',
                'Does not include monitoring platform licenses'
            ],
            price: '$490.00',
            period: 'Monthly',
            link: 'https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-centralized-logging-and-monitoring',
            featured: true
        },
        {
            id: 'devsecops-daily',
            icon: 'la-user-cog',
            name: 'DevSecOps ONE - Daily Subscription',
            tagline: 'Dedicated DevSecOps Engineer on Demand',
            description: 'Need custom development, complex integrations or help with your architecture? DevSecOps ONE – Daily Subscription gives you a dedicated DevSecOps Engineer on demand.',
            details: [
                'Unlimited requests',
                'One active at a time',
                'Average 48–72h turnaround',
                'Pause/cancel anytime',
                'Custom development',
                'Complex integrations',
                'Architecture consulting'
            ],
            price: '$519.90',
            period: 'Daily',
            link: 'https://my.epicfinal.com/index.php?rp=/store/managed-services/devsecops-one-daily-subscription',
            featured: false
        },
        {
            id: 'devsecops-weekly',
            icon: 'la-user-shield',
            name: 'DevSecOps ONE - Weekly Subscription',
            tagline: 'Dedicated DevSecOps Engineer on Demand',
            description: 'Need custom development, complex integrations or help with your architecture? DevSecOps ONE – Weekly Subscription gives you a dedicated DevSecOps Engineer on demand.',
            details: [
                'Unlimited requests',
                'One active at a time',
                'Average 48–72h turnaround',
                'Pause/cancel anytime',
                'Custom development',
                'Complex integrations',
                'Architecture consulting'
            ],
            price: '$2,490.90',
            period: 'Weekly',
            link: 'https://my.epicfinal.com/index.php?rp=/store/managed-services/devsecops-one-weekly-subscription',
            featured: false
        }
    ];

    return (
        <>
            <Helmet>
                <title>Managed Services - Expert Infrastructure Management | Epicfinal</title>
                <meta name="description" content="Professional managed services including CDN management, cloud infrastructure, monitoring, AWS Auto Scaling, and dedicated DevSecOps engineers. 24/7 expert support." />
                <meta name="keywords" content="managed services, CDN management, Cloudflare, AWS management, server monitoring, DevSecOps, cloud infrastructure" />
            </Helmet>

            <div className="managed-services-container">
                {/* Hero Section */}
                <section className="managed-hero">
                    <div className="container">
                        <div className="managed-hero-content">
                            <h1 className="managed-hero-title">
                                Professional Managed Services
                            </h1>
                            <p className="managed-hero-subtitle">
                                Expert infrastructure management so you can focus on growing your business
                            </p>
                            <p className="managed-hero-description">
                                From CDN optimization to full AWS management, our team of certified engineers handles your infrastructure with monitoring, proactive maintenance, and rapid response.
                            </p>
                            <div className="managed-hero-stats">
                                <div className="stat-item">
                                    <i className="las la-clock"></i>
                                    <div className="stat-content">
                                        <span className="stat-number">24/7</span>
                                        <span className="stat-label">Monitoring</span>
                                    </div>
                                </div>
                                <div className="stat-item">
                                    <i className="las la-headset"></i>
                                    <div className="stat-content">
                                        <span className="stat-number">Expert</span>
                                        <span className="stat-label">Support</span>
                                    </div>
                                </div>
                                <div className="stat-item">
                                    <i className="las la-shield-alt"></i>
                                    <div className="stat-content">
                                        <span className="stat-number">Proactive</span>
                                        <span className="stat-label">Security</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Services Grid */}
                <section className="managed-services-section">
                    <div className="container">
                        <div className="section-header">
                            <h2>Our Managed Services</h2>
                            <p>Comprehensive solutions for every infrastructure need</p>
                        </div>

                        <div className="services-grid">
                            {services.map((service) => (
                                <div 
                                    key={service.id} 
                                    className={`service-card ${service.featured ? 'featured' : ''}`}
                                    id={service.id}
                                >
                                    {service.featured && (
                                        <div className="featured-badge">Most Popular</div>
                                    )}
                                    
                                    <div className="service-header">
                                        <div className="service-icon">
                                            <i className={`las ${service.icon}`}></i>
                                        </div>
                                        <h3 className="service-name">{service.name}</h3>
                                        <p className="service-tagline">{service.tagline}</p>
                                    </div>

                                    <div className="service-body">
                                        <p className="service-description">{service.description}</p>

                                        <ul className="service-features">
                                            {service.details.map((detail, index) => (
                                                <li key={index}>
                                                    <i className="las la-check-circle"></i>
                                                    <span>{detail}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="service-footer">
                                        <div className="service-pricing">
                                            <span className="price">{service.price}</span>
                                            <span className="period">/{service.period}</span>
                                        </div>
                                        <a 
                                            href={service.link} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="btn-order"
                                        >
                                            Order Now
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Why Choose Section */}
                <section className="why-choose-section">
                    <div className="container">
                        <div className="section-header">
                            <h2>Why Choose Our Managed Services?</h2>
                            <p>Professional expertise for your infrastructure</p>
                        </div>

                        <div className="why-grid">
                            <div className="why-item">
                                <div className="why-icon">
                                    <i className="las la-user-tie"></i>
                                </div>
                                <h4>Expert Team</h4>
                                <p>Certified engineers with years of experience in cloud infrastructure, security, and DevOps practices</p>
                            </div>

                            <div className="why-item">
                                <div className="why-icon">
                                    <i className="las la-clock"></i>
                                </div>
                                <h4>Proactive Monitoring</h4>
                                <p>monitoring with intelligent alerts and automated responses to keep your systems healthy</p>
                            </div>

                            <div className="why-item">
                                <div className="why-icon">
                                    <i className="las la-shield-alt"></i>
                                </div>
                                <h4>Security First</h4>
                                <p>Continuous security updates, vulnerability assessments, and compliance management</p>
                            </div>

                            <div className="why-item">
                                <div className="why-icon">
                                    <i className="las la-chart-line"></i>
                                </div>
                                <h4>Performance Optimization</h4>
                                <p>Regular performance tuning and optimization to ensure maximum efficiency</p>
                            </div>

                            <div className="why-item">
                                <div className="why-icon">
                                    <i className="las la-dollar-sign"></i>
                                </div>
                                <h4>Cost Effective</h4>
                                <p>Reduce IT overhead while improving service quality and reliability</p>
                            </div>

                            <div className="why-item">
                                <div className="why-icon">
                                    <i className="las la-expand-arrows-alt"></i>
                                </div>
                                <h4>Scalable Solutions</h4>
                                <p>Services that grow with your business needs and adapt to changing requirements</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="managed-cta">
                    <div className="container">
                        <div className="cta-content">
                            <h2>Ready to Simplify Your Infrastructure Management?</h2>
                            <p>Let our experts handle your infrastructure while you focus on growing your business</p>
                            <div className="cta-buttons">
                                <a 
                                    href="https://meetings.hubspot.com/viacheslav-seifer" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="btn-cta btn-primary"
                                >
                                    Schedule a Consultation
                                </a>
                                <a 
                                    href="mailto:support@epicfinal.com" 
                                    className="btn-cta btn-secondary"
                                >
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default ManagedServices;
